import React, { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  MenuItem,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import Layout from "../components/layout/Layout";
import Panel from "../components/brand/Panel";
import DataTable from "../components/brand/DataTable";
import { getSales } from "../api/salesApi";
import { formatDateUk } from "../utils/dateDisplay";
import { getApiErrorMessage } from "../utils/concurrency";

const PAGE_SIZES = [10, 25, 50];
const EMPTY_COPY = "A sale appears here when a resident submits conveyancer details in the client portal.";

const dash = (value) => (value ? value : "—");

export default function SalesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, Number(searchParams.get("page")) || 1);
  const requestedSize = Number(searchParams.get("pageSize"));
  const pageSize = PAGE_SIZES.includes(requestedSize) ? requestedSize : 10;
  const stage = searchParams.get("stage") || "open";
  const q = searchParams.get("q") || "";
  const [draft, setDraft] = useState(q);
  const [data, setData] = useState({ results: [], total: 0, stages: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setDraft(q);
  }, [q]);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    setLoading(true);
    setError("");
    getSales({ page, page_size: pageSize, stage, q }, { signal: controller.signal })
      .then((payload) => {
        if (!cancelled) setData(payload);
      })
      .catch((err) => {
        if (cancelled || err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
        setError(getApiErrorMessage(err, "Sales could not be loaded."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [page, pageSize, stage, q]);

  const updateParams = (changes) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([key, value]) => {
      if (value === undefined || value === null || value === "") next.delete(key);
      else next.set(key, String(value));
    });
    if (!("page" in changes)) next.delete("page");
    setSearchParams(next);
  };

  const stages = data.stages?.length ? data.stages : [{ key: stage, label: stage === "open" ? "Open" : stage }];

  return (
    <Layout eyebrow="Sales" title="Sales">
      <Panel
        title="Sales"
        right={
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <TextField
              size="small"
              label="Search"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") updateParams({ q: draft.trim() });
              }}
            />
            <Button
              variant="outlined"
              startIcon={<SearchIcon />}
              onClick={() => updateParams({ q: draft.trim() })}
            >
              Search
            </Button>
            <TextField
              select
              size="small"
              label="Stage"
              value={stages.some((item) => item.key === stage) ? stage : "open"}
              onChange={(event) => updateParams({ stage: event.target.value === "open" ? "" : event.target.value })}
              sx={{ minWidth: 220 }}
            >
              {stages.map((item) => (
                <MenuItem key={item.key} value={item.key}>
                  {item.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        }
      >
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {!loading && !error && data.results?.length === 0 && (
          <Typography sx={{ px: 2.75, py: 3 }} color="text.secondary">
            {EMPTY_COPY}
          </Typography>
        )}
        {!loading && !error && data.results?.length > 0 && (
          <DataTable>
            <TableHead>
              <TableRow>
                <TableCell>Received</TableCell>
                <TableCell>Property</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Conveyancer</TableCell>
                <TableCell>Stage</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.results.map((sale) => (
                <TableRow
                  key={sale.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onDoubleClick={(event) => {
                    if (event.target.closest("a")) return;
                    navigate(`/sales/${sale.id}`);
                  }}
                >
                  <TableCell>{formatDateUk(sale.received_at)}</TableCell>
                  <TableCell>
                    {sale.property_id ? (
                      <Link component={RouterLink} to={`/properties/${sale.property_id}/details`}>
                        {dash(sale.property_name)}
                      </Link>
                    ) : (
                      dash(sale.property_name)
                    )}
                  </TableCell>
                  <TableCell>
                    {sale.unit_id ? (
                      <Link component={RouterLink} to={`/units/${sale.unit_id}/details`}>
                        {dash(sale.unit_name)}
                      </Link>
                    ) : (
                      dash(sale.unit_name)
                    )}
                  </TableCell>
                  <TableCell>{dash(sale.owner_name)}</TableCell>
                  <TableCell>
                    <Link component={RouterLink} to={`/sales/${sale.id}`}>
                      {sale.conveyancer_company || "Open sale"}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link component={RouterLink} to={`/sales/${sale.id}`}>
                      {sale.status_label}
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </DataTable>
        )}
      </Panel>
      <TablePagination
        component="div"
        count={data.total || 0}
        page={Math.max(0, page - 1)}
        onPageChange={(_event, nextPage) => updateParams({ page: nextPage + 1 === 1 ? "" : String(nextPage + 1) })}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(event) => updateParams({ pageSize: event.target.value, page: "" })}
        rowsPerPageOptions={PAGE_SIZES}
      />
    </Layout>
  );
}
